import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { newsParser } from '../../util/newsParser';
import { teaserTextParser } from '../../util/teaserTextParser';
import Card from './Card';

import { useHandleScreenResize } from '../../hooks/app/useHandleScreenResize';

// ICONS
import IconSpeechBubble from '../Icons/IconSpeechBubble';
import CardGridPlaceholderElement from './CardGridPlaceholderElement';
import CarouselNew from '../Carousel/CarouselNew';
import { useEffect, useRef, useState } from 'react';
import IconExternalLink from '../Icons/IconExternalLink';

function NewsCardsRenderer({ item, topic }) {
  const root = useSelector((state) => state.app.root);
  const dragging = useSelector((state) => state.app.dragging);

  // Memoize parsed text and CSS classes
  const parsedText = useMemo(() => {
    const titleLength = item.title?.length || 0;
    let teaserLength = 100;

    if (titleLength < 40) teaserLength = 180;
    else if (titleLength < 80) teaserLength = 120;
    else if (titleLength < 120) teaserLength = 80;
    else teaserLength = 40;

    return teaserTextParser(item.summary, teaserLength);
  }, [item.title, item.summary]);

  const cardCssClasses = 'news-card';

  // Memoize preventDefault handler
  const handleClick = useCallback(
    (event) => {
      if (dragging) {
        event.preventDefault();
      }
    },
    [dragging],
  );

  return item.external ? (
    <a href={item.source} onClick={handleClick} title={item.title} className={cardCssClasses} target="_blank" rel="noopener noreferrer">
      <Card cardStyle="topImage" cssClasses="bg-white with-shadow" imagePath={item.header_picture !== '' ? item.header_picture : null} icon={item.header_picture === '' ? <IconSpeechBubble /> : null}>
        <div className="news-card__label immo-card__label--active">
          <IconExternalLink />
          <span className="text-white">externer Artikel</span>
        </div>
        <div className="card__content__text">
          <p className="font-110 font-weight-600 lh-130">{item.title}</p>
          <p className="cardMeta">
            {item.published_on ? item.published_on : item.updated_at}
            {topic && item.topics.length > 0 ? ` in ${item.topics}` : ''}
          </p>

          <div className="news-card__teaser">
            <p className="cardText">{parsedText}</p>
          </div>
        </div>
        <span className="card__readmorelink">Mehr lesen</span>
      </Card>
    </a>
  ) : (
    <Link onClick={(event) => dragging && event.preventDefault()} to={root + '/news/' + item.slug} title={item.title} className={cardCssClasses} target="_self">
      <Card cardStyle="topImage" cssClasses="bg-white with-shadow" imagePath={item.header_picture !== '' ? item.header_picture : null} icon={item.header_picture === '' ? <IconSpeechBubble /> : null}>
        <>
          <div className="card__content__text">
            <p className="font-110 font-weight-600 lh-130">{item.title}</p>
            <p className="cardMeta">
              {item.published_on ? item.published_on : item.updated_at}
              {topic && item.topics.length > 0 ? ` in ${item.topics}` : ''}
            </p>

            <div className="news-card__teaser">
              <p className="cardText">{parsedText}</p>
            </div>
          </div>
          <span className="card__readmorelink">Mehr lesen</span>
        </>
      </Card>
    </Link>
  );
}

// Memoize NewsCardsRenderer
const MemoizedNewsCardsRenderer = React.memo(NewsCardsRenderer);

function NewsCards({ news, selectedFilter, filterOptions, carousel, cssClasses, topic }) {
  const { isMobile } = useHandleScreenResize();

  // Memoize parsed news and filtered news
  const parsedNews = useMemo(() => news.map((n) => newsParser(n)), [news]);

  const filteredNews = useMemo(() => {
    if (selectedFilter > 0) {
      const filterSlug = filterOptions[selectedFilter].slug;
      return parsedNews.filter((item) => item.topicsArray.some((t) => t.slug === filterSlug));
    }
    return parsedNews;
  }, [parsedNews, selectedFilter, filterOptions]);

  if (!filteredNews || filteredNews.length === 0) return null;

  const finalCssClasses = cssClasses || '';

  if (!carousel) {
    return (
      <div className={`cardgrid ${finalCssClasses}`}>
        {filteredNews.map((n) => (
          <MemoizedNewsCardsRenderer topic={topic} item={n} key={n.slug} />
        ))}
        <CardGridPlaceholderElement array={filteredNews} />
      </div>
    );
  }

  if (filteredNews.length > 3) {
    return (
      <CarouselNew isMobile={isMobile} cssClasses={isMobile && 'carousel-wrapper--news-mobile'}>
        {filteredNews.map((n) => (
          <MemoizedNewsCardsRenderer item={n} key={n.slug} />
        ))}
      </CarouselNew>
    );
  }

  return (
    <div className="w-desktop--center">
      <div className={`cardgrid ${finalCssClasses}`}>
        {filteredNews.map((n) => (
          <MemoizedNewsCardsRenderer item={n} key={n.slug} />
        ))}

        <CardGridPlaceholderElement array={filteredNews} />
      </div>
    </div>
  );
}

export default React.memo(NewsCards);
